import type { RequestOptions } from "@@/plugin-request/request";
import { getLocale, type RequestConfig, history, getIntl } from "@umijs/max";
import { message } from "antd";

/**
 * @name 与后端约定的响应数据格式
 */
interface ResponseStructure {
  success?: boolean;
  code?: number;
  data: any;
}

const loginPath = "/login";

/**
 * @name 错误处理
 * pro 自带的错误处理， 可以在这里做自己的改动
 * @doc https://umijs.org/docs/max/request#配置
 */
export const errorConfig: RequestConfig = {
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res) => {
      const { success, code, data } = res as unknown as ResponseStructure;

      // 兼容 code: 0 为成功
      const isSuccess = success === true || code === 0 || code === 200;

      if (!isSuccess) {
        const error: any = new Error((res as any).msg || (res as any).message);
        error.name = "BizError";
        error.info = res;
        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;
      const intl = getIntl();
      // 我们的 errorThrower 抛出的错误。
      if (error.name === "BizError") {
        const errorInfo = error.info;
        if (errorInfo) {
          const msg = errorInfo.msg || errorInfo.message;
          message.error(
            msg || intl.formatMessage({ id: "pages.common.error.failed" }),
          );
          error.isGlobalHandled = true;
        }
      } else if (error.response && error.response.status !== 0) {
        // Axios 的错误
        const { status, data } = error.response;

        if (status === 401) {
          const { location } = history;
          const normalizedPathname =
            location.pathname.endsWith("/") && location.pathname !== "/"
              ? location.pathname.slice(0, -1)
              : location.pathname;

          if (
            normalizedPathname !== loginPath &&
            normalizedPathname !== "/main_login"
          ) {
            history.push(loginPath);
          }
        }

        const errorMessage =
          data?.msg ||
          data?.message ||
          data?.errorMessage ||
          (intl.messages[`pages.common.error.${status}`]
            ? intl.formatMessage({ id: `pages.common.error.${status}` })
            : intl.formatMessage(
                { id: "pages.common.error.response" },
                { status },
              ));

        message.error(errorMessage);
        error.isGlobalHandled = true;
      } else if (
        error.request ||
        ["Network Error", "ERR_NETWORK", "ERR_INTERNET_DISCONNECTED"].includes(
          error.message || error.code,
        )
      ) {
        if (
          error.code === "ECONNABORTED" ||
          error.message?.includes("timeout")
        ) {
          error.message = intl.formatMessage({
            id: "pages.common.error.timeout",
          });
        } else {
          error.message = intl.formatMessage({
            id: "pages.common.error.network",
          });
        }
        message.error(error.message);
        error.isGlobalHandled = true; // 标记为已由全局错误处理器处理
      } else if (error.message && error.message !== "Network Error") {
        message.error(
          error.message ||
            intl.formatMessage({ id: "pages.common.error.default" }),
        );
        error.isGlobalHandled = true;
      }
      throw error; // 总是抛出错误，以确保调用者逻辑被中断，并由 isGlobalHandled 防止重复提示
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: RequestOptions) => {
      const token = localStorage.getItem("token");
      const headers = {
        ...config.headers,
        Language: getLocale(),
        Authorization: token ? `Bearer ${token}` : "",
      };
      return { ...config, headers };
    },
  ],

  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 可以在这里根据后端返回的数据结构进行统一的数据预处理
      return response;
    },
  ],
};
